import axios from "axios";
import { auth } from "../firebase";
import { getCurrentLocale } from "@/i18n/locale";

export const API_BASE_URL =
  import.meta.env.VITE_API_URL || "http://localhost:8080";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

api.interceptors.request.use(async (config) => {
  const headers = config.headers ?? {};
  const locale = getCurrentLocale();
  if (typeof (headers as any).set === "function") {
    (headers as any).set("Accept-Language", locale);
    (headers as any).set("X-Nobori-Locale", locale);
  } else {
    (headers as any)["Accept-Language"] = locale;
    (headers as any)["X-Nobori-Locale"] = locale;
  }
  config.headers = headers;

  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return config;

    const token = await currentUser.getIdToken();
    if (!token) return config;

    if (typeof (headers as any).set === "function") {
      (headers as any).set("Authorization", `Bearer ${token}`);
    } else {
      (headers as any).Authorization = `Bearer ${token}`;
    }
    config.headers = headers;
  } catch {
    // Ignore token attachment failures; backend session cookie can still authenticate.
  }
  return config;
});

export interface LoginData {
  idToken: string;
}

type Brand = {
  brandId: string;
  country: string;
  name: string;
  url: string;
  id: string;
  companyId?: string | null;
  company?: CompanySummary | null;
  currentFocus: string;
  activeProviders?: string[] | null;
  created_at?: string | Date | null;
  createdAt?: string | Date | null;
  accessRole?: "company_admin" | "company_member" | "brand_member";
};

export type CompanySummary = {
  id?: string;
  companyId?: string;
  name?: string;
  country?: string | null;
  url?: string;
  createdById?: string | null;
  role?: "company_admin" | "company_member";
};

export type Collaborator = {
  id: string;
  email: string;
  status: "pending" | "accepted" | "revoked";
  inviteMessage?: string | null;
  createdAt: string;
  brand: {
    id: string;
    name: string;
  };
};

export type CompanyMemberSummary = {
  id: string;
  email: string;
  role: "company_admin" | "company_member";
};

export interface InviteResolveResponse {
  ok: boolean;
  invite: {
    email: string;
    companyId: string | null;
    roleType: "company" | "brand";
    brandIds: string[];
    expiresAt: string;
    status: string;
  };
}

export interface InviteAcceptResponse {
  ok: boolean;
  landing: {
    companyId: string | null;
    brandId: string | null;
  };
}

export interface InviteListResponse {
  ok: boolean;
  invites: {
    id: string;
    email: string;
    roleType: "company" | "brand";
    companyId?: string | null;
    companyName?: string | null;
    brandIds: string[];
    brandNames: string[];
    status: string;
    expiresAt: string;
    acceptedAt?: string | null;
    createdAt: string;
  }[];
}

export interface BrandResponse {
  ok: boolean;
  brand: Brand & Omit<Brand, "brandId">;
}

export interface Plan {
  currency: string;
  description: string;
  id: string;
  active: boolean;
  custom?: boolean;
  customText?: {
    prompts: string;
    brands: string;
    countries: string;
    runs: string;
    export: string;
    support: string;
  };
  features: string[];
  highlighted: any;
  limits: {
    "prompts.max_tracked": number;
    "prompts.max_suggested": number;
    "brands.max": number;
    "countries.max": number;
    "exports.csv.enabled": number;
    "credits.monthly": number;
    "runs.monthly": number;
    "ai.chatgpt.enabled": boolean;
    "ai.mode.enabled": boolean;
    "ai.aimode.enabled"?: boolean;
    "ai.overviews.enabled"?: boolean;
    "ai.gemini.enabled"?: boolean;
    "ai.perplexity.enabled": boolean;
    "ai.grok.enabled"?: boolean;
    "ai.copilot.enabled"?: boolean;
  };
  metadata: { label: string | null };
  monthlyPrice: string;
  monthlyPriceId: string;
  name: string;
  yearlyPrice: string;
  yearlyPriceId: string;
}

export interface MeUsageResponse {
  ok: boolean;
  brands?: {
    used: number;
    limit: number;
    remaining: number;
  };
  prompts: {
    used: number;
    limit: number;
    remaining: number;
  };
  runs: {
    used: number;
    limit: number;
    remaining: number;
  };
  userId: string;
  usageOwnerId?: string;
}

export interface ProviderOverridesResponse {
  ok: boolean;
  ownerUserId: string;
  isReadOnly: boolean;
  overrides: {
    chatgpt: boolean | null;
    perplexity: boolean | null;
    overviews: boolean | null;
    aiMode: boolean | null;
    gemini: boolean | null;
    grok: boolean | null;
    copilot: boolean | null;
  };
  effective: {
    chatgpt: boolean;
    perplexity: boolean;
    overviews: boolean;
    aiMode: boolean;
    gemini: boolean;
    grok: boolean;
    copilot: boolean;
  };
  effectiveProviders: string[];
}

export interface MeResponse {
  ok: boolean;
  user: {
    onboard_step: number;
    requires_plan_selection?: boolean;
    email: string;
    id: string;
    expired_at: string;
    tier: string;
    name: string;
    isWhitelisted?: boolean;
  };
  brands: Brand[];
  company: CompanySummary;
  companies?: CompanySummary[];
}

export interface VerificationEmailSendResponse {
  ok: boolean;
  skipped?: boolean;
  reason?: string;
}

export interface VerificationEmailResolveResponse {
  ok: boolean;
  redirectUrl: string;
}

export interface DailyAlertSettingsPayload {
  brandId: string;
  enabled: boolean;
  sendTime: string;
  timezone: string;
  includeVisibilityChanges: boolean;
  includeCompetitorMentions: boolean;
  includeActionsRecommendations: boolean;
  includePromptStatus: boolean;
  updatedAt: string | null;
}

export interface DailyAlertSettingsResponse {
  ok: boolean;
  settings: DailyAlertSettingsPayload;
}

export type PaginatedResponseMeta = {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};

export type Prompt = {
  country: string;
  text: string;
  brand: string;
  cluster?: string;
  is_active: boolean;
  id: string;
  created_at: Date;
  updated_at: Date;
  last_run_status?: string | null;
  provider_statuses?: Record<string, string> | null;
  provider_status_details?: Record<
    string,
    {
      status?: string | null;
      runDate?: string | null;
      lastRunAt?: string | null;
      queuedAt?: string | null;
      startedAt?: string | null;
      completedAt?: string | null;
      failedAt?: string | null;
      lastTransitionAt?: string | null;
      errorReason?: string | null;
      snapshotId?: string | null;
    }
  > | null;
  provider_run_summary?: {
    total: number;
    pending: number;
    completed: number;
    failed: number;
    skipped: number;
    terminal: number;
    completionRatio: number;
  } | null;
  has_provider_run_in_progress?: boolean | null;
  visibility?: number | null;
  position?: number | null;
  positionsByRun?: {
    runId: string;
    positions: Record<string, number>;
    isMainMentioned?: boolean;
  }[];
};

export type PromptCluster = {
  id: string;
  title: string;
  country: string;
  brandId: string | null;
  boostStatus?: string | null;
  strategyStatus?: string | null;
  boostLastGeneratedAt?: string | null;
  boostTaskCount?: number | null;
  boostCooldownDays?: number | null;
  boostUnlockAt?: string | null;
  boostLocked?: boolean;
  trial_allowed_cluster_id?: string | null;
  trial_boost_used?: boolean;
  prompts: Prompt[];
};

export type GeneratedCluster = {
  title: string;
  clusterId?: string;
};

export type GeneratedClusterPrompts = {
  title: string;
  clusterId?: string;
  prompts: { text: string; country: string }[];
};

export type SuggestedPromptsGenerateMode = "single_cluster" | "all_clusters";

export type SuggestedPromptsGenerateBlockedReason =
  | "daily_cap_reached"
  | "cluster_daily_cap_reached"
  | "plan_quota_reached"
  | "no_clusters";

export type SuggestedPromptsGenerateSuccessResponse = {
  ok: true;
  status: "ok";
  prompts: { text: string; country: string }[];
  generated_count: number;
  remaining_quota_after: number;
  cap_today: number;
  generated_today: number;
};

export type SuggestedPromptsGenerateBlockedResponse = {
  ok: false;
  status: "blocked";
  reason: SuggestedPromptsGenerateBlockedReason;
  allowed: 0;
  remaining_quota: number;
  cap_today: number;
  generated_today: number;
  prompts: [];
};

export type SuggestedPromptsGenerateErrorResponse = {
  ok: false;
  error: string;
  status?: "error";
};

export type SuggestedPromptsGenerateResponse =
  | SuggestedPromptsGenerateSuccessResponse
  | SuggestedPromptsGenerateBlockedResponse
  | SuggestedPromptsGenerateErrorResponse;

export type Actionable = {
  id: string;
  url: string;
  domain?: string | null;
  type: string;
  used: number;
  missedAnswers: number;
  mentionedBrands: { id: string; name: string; url: string }[];
  providers: string[];
  prompts: { id: string; text: string }[];
  lastSeenAt: string | null;
  status: "toAddress" | "inProgress" | "resolved" | "canceled";
  impactLevel: "low" | "medium" | "high";
  effortLevel: "low" | "medium" | "high";
  priorityScore: number;
  lastContactedAt: string | null;
  contactName: string | null;
  contactEmail: string | null;
  lastEmailSubject: string | null;
  lastEmailBody: string | null;
};

export type FoundationCheck = {
  key: string;
  status: "Good" | "Bad" | "Implemented";
  evidence?: Record<string, any>;
};

export type VisibilityTaskOriginalCopy = {
  title: string | null;
  description: string | null;
  rationale: string | null;
  impact: string | null;
  subtasks?: Record<string, any>[] | null;
};

export type FoundationTask = {
  id: string;
  checkKey: string | null;
  status: "toAddress" | "inProgress" | "resolved" | "canceled";
  priority: "High" | "Medium" | "Low" | null;
  taskType: string | null;
  title: string | null;
  description: string | null;
  rationale: string | null;
  clusterId: string;
  impact: string | null;
  evidence: Record<string, any>;
  subtasks: Record<string, any>[] | null;
  original?: VisibilityTaskOriginalCopy | null;
  sortIndex?: number | null;
  updatedAt: string | null;
};

export type FoundationSummary = {
  lastCheckedAt: string | null;
  status: string | null;
  taskCount: number | null;
  checks: FoundationCheck[];
  tasks: FoundationTask[];
};

export type ClusterAction = {
  id: string;
  clusterId: string | null;
  status: "toAddress" | "inProgress" | "resolved" | "canceled";
  priority: "High" | "Medium" | "Low" | null;
  taskType: string | null;
  title: string | null;
  description: string | null;
  rationale: string | null;
  impact: string | null;
  evidence: Record<string, any>;
  subtasks: Record<string, any>[] | null;
  original?: VisibilityTaskOriginalCopy | null;
  sortIndex?: number | null;
  updatedAt: string | null;
  createdAt: string | null;
};

export type StrategyTimeRange = {
  type: "last_7" | "last_30" | "last_90" | "all_time";
  start: string;
  end: string;
};

export type StrategyCoverage = {
  total_prompts: number;
  completed_prompts: number;
  coverage_percent: number;
  elapsed_seconds: number;
  started_at: string | null;
  ready: boolean;
  ready_reason: "coverage" | "fallback" | null;
};

export type StrategyPromptSummary = {
  prompt_id: string;
  prompt_text: string;
  platform: string | null;
};

export type StrategyCompetitorRow = {
  brand_id: string;
  brand_name: string;
  brand_domain: string | null;
  visibility: number;
  avg_position: number | null;
  is_main?: boolean;
};

export type StrategyCitedPage = {
  url: string;
  domain: string | null;
  favicon_url: string | null;
  url_type: string;
  used_total: number;
};

export type StrategyCitedDomain = {
  domain: string;
  domain_type: "Publisher" | "Competitor" | "Community" | "Owned" | "Other";
  used_total: number;
};

export type StrategyPlanTask = {
  id: string;
  title: string | null;
  task_type: string | null;
  description: string | null;
  rationale: string | null;
  evidence: Record<string, unknown>;
  impact: string | null;
  original?: VisibilityTaskOriginalCopy | null;
  priority: "High" | "Medium" | "Low" | null;
  status: "toAddress" | "inProgress" | "resolved" | "canceled" | null;
  updated_at: string | null;
  created_at: string | null;
};

export type StrategyStateSummary = {
  status:
    | "loading"
    | "queued"
    | "generating"
    | "ready"
    | "failed"
    | "not_started"
    | "skipped_full_visibility"
    | null;
  source: string | null;
  updated_at: string | null;
  status_updated_at: string | null;
};

export type StrategySnapshotData = {
  brand_id: string;
  cluster_id: string | null;
  cluster_name: string | null;
  time_range: StrategyTimeRange;
  coverage: StrategyCoverage;
  visibility_score: number;
  average_position: number | null;
  prompts: StrategyPromptSummary[];
  competitors: StrategyCompetitorRow[];
  cited_pages: StrategyCitedPage[];
  cited_domains: StrategyCitedDomain[];
  plan: {
    status: "queued" | "generating" | "ready" | "not_ready";
    tasks_ready: boolean;
  };
  regenerate_status: "allowed" | "cooldown" | "queued" | "generating";
  next_regenerate_at: string | null;
  cooldown_days: number | null;
  trial_allowed_cluster_id: string | null;
  trial_boost_used: boolean;
  strategy_boost_job_queued: boolean;
  strategy_state?: StrategyStateSummary | null;
};

export type StrategyPlanData = {
  brand_id: string;
  cluster_id: string | null;
  cluster_name: string | null;
  time_range: StrategyTimeRange;
  coverage: StrategyCoverage;
  status: "queued" | "generating" | "ready" | "not_ready";
  tasks_ready: boolean;
  tasks: StrategyPlanTask[];
  regenerate_status: "allowed" | "cooldown" | "queued" | "generating";
  next_regenerate_at: string | null;
  cooldown_days: number | null;
  trial_allowed_cluster_id: string | null;
  trial_boost_used: boolean;
  strategy_boost_job_queued: boolean;
  strategy_state?: StrategyStateSummary | null;
};

export type StrategyRegenerateStatusData = {
  brand_id: string;
  cluster_id: string | null;
  cluster_name: string | null;
  boost_status: string;
  regenerate_status: "allowed" | "cooldown" | "queued" | "generating";
  next_regenerate_at: string | null;
  cooldown_days: number | null;
  trial_allowed_cluster_id: string | null;
  trial_boost_used: boolean;
  strategy_state?: StrategyStateSummary | null;
  coverage: StrategyCoverage;
};

export type StrategyRegenerateResponse = {
  ok: boolean;
  messageId?: string;
  status?: string;
  regenerate_status?: "allowed" | "cooldown" | "queued" | "generating";
  next_regenerate_at?: string | null;
  cooldown_days?: number | null;
  trial_allowed_cluster_id?: string | null;
  trial_boost_used?: boolean;
  code?: string;
  message?: string;
};

export type OnboardingSnapshotMeta = {
  brand_id: string;
  generated_at: string;
  sample_size_target: number;
  sample_size_used: number;
  is_partial: boolean;
  status: "ready" | "partial" | "pending" | "empty";
  poll_after_ms: number;
};

export type OnboardingSnapshotVisibility = {
  percentage: number;
  average_position?: number | null;
  best_position?: number | null;
  explanation: string;
  competitor_advantage: boolean | null;
};

export type OnboardingSnapshotCoMentionedBrand = {
  name: string;
  domain: string | null;
  share_percent: number;
  average_position?: number | null;
};

export type OnboardingSnapshotDomainItem = {
  domain: string;
  domain_type: string;
  share_percent: number;
  citation_count?: number;
  pages?: Array<{
    url: string;
    title: string | null;
    citation_count: number;
  }>;
};

export type OnboardingSnapshotPromptMention = {
  prompt_id: string;
  prompt_text: string;
  cluster_title: string | null;
  mention_rate: number;
};

export type OnboardingSnapshotPromptGap = {
  prompt_id: string;
  prompt_text: string;
  cluster_title: string | null;
  top_competitor: string | null;
  gap_percent: number;
};

export type OnboardingSnapshotPromptResult = {
  prompt_id: string;
  prompt_text: string;
  target_mentioned: boolean;
  target_position: number | null;
  mentioned_brands: Array<{
    name: string;
    domain: string | null;
    position: number;
  }>;
  citations: Array<{
    url: string;
    domain: string;
    title: string | null;
  }>;
  next_step: string | null;
};

export type OnboardingSnapshotData = {
  meta: OnboardingSnapshotMeta;
  visibility: OnboardingSnapshotVisibility;
  comentioned_brands: OnboardingSnapshotCoMentionedBrand[];
  cited_domains: {
    available: boolean;
    mode: "full" | "fallback";
    items: OnboardingSnapshotDomainItem[];
    fallback_copy: string | null;
  };
  where_you_appear_most: OnboardingSnapshotPromptMention[];
  where_you_are_missing: OnboardingSnapshotPromptGap[];
  prompt_results?: OnboardingSnapshotPromptResult[];
  next_steps: string[];
  providers: {
    attempt_order: string[];
    used: string[];
    queued_for_refresh: string[];
  };
};

export interface ChartData {
  chartData: any;
  citedDomainsVisibility: any;
  citedBrandsVisibility?: Record<string, number>;
  competitorsVisibility?: Record<string, number>;
  competitorsPosition?: Record<string, number>;
  citedPagesStats: any;
  mentionedPagesStats?: any;
  data: any;
  promptRunCount?: number;
  webQueries?: { query: any; provider: string }[];
  mainBrandVisibility: any;
  promptStatus?: string;
  promptLastRun?: string | null;
  promptProviders?: string[];
  promptProviderStatuses?: Record<string, string>;
  promptProviderStatusHistory?: Array<{
    provider: string;
    status: string;
    runDate: string;
    lastRunAt: string | null;
    queuedAt?: string | null;
    startedAt?: string | null;
    completedAt?: string | null;
    failedAt?: string | null;
    lastTransitionAt?: string | null;
    errorReason?: string | null;
    snapshotId?: string | null;
  }>;
  promptProviderStatusDetails?: Record<
    string,
    {
      status?: string | null;
      lastRunAt?: string | null;
      queuedAt?: string | null;
      startedAt?: string | null;
      completedAt?: string | null;
      failedAt?: string | null;
      lastTransitionAt?: string | null;
      errorReason?: string | null;
      snapshotId?: string | null;
    }
  >;
  hasPendingExecutions?: boolean;
  actionables?: Actionable[];
}

export type OnboardingGuideStepName =
  "activate_competitors" | "review_prompt_results" | "boost_cluster";

export interface OnboardingGuideState {
  brandId: string;
  companyId: string | null;
  dismissed: boolean;
  completedOverlaySeen: boolean;
  completedAt: string | null;
  isCompleted: boolean;
  isVisible: boolean;
  progress: {
    completed: number;
    total: number;
  };
  runningPrompts: {
    completed: number;
    total: number;
    done: boolean;
  };
  steps: {
    completeSetup: boolean;
    runningPrompts: boolean;
    activateCompetitors: boolean;
    reviewPromptResults: boolean;
    boostCluster: boolean;
  };
  nextStep:
    | "running_prompts"
    | "activate_competitors"
    | "review_prompt_results"
    | "boost_cluster"
    | null;
  reviewPromptTargetPromptId: string | null;
  boostTargetClusterId: string | null;
}

export interface CompetitorDashboardWidgetState {
  phase: "loading" | "results";
  ready: boolean;
  totalPrompts: number;
  completedPrompts: number;
  totalExecutions?: number;
  completedExecutions?: number;
  elapsedSeconds: number;
  orgId: string | null;
  autoAdded: boolean;
  autoAddedCount?: number;
}

export type DataSourceProvider = "nobori_visibility" | "search_console" | "ga4";
export type DataSourceStatus = "connected" | "disconnected" | "action_required";

export type DataChatSource = {
  provider: DataSourceProvider;
  label: string;
  status: DataSourceStatus;
  description: string;
  propertyName: string | null;
  connectedAt: string | null;
  authMode?: string | null;
  siteSelectionRequired?: boolean;
  propertySelectionRequired?: boolean;
  propertyMismatch?: boolean;
  availableInChat: boolean;
};

export type SearchConsoleSite = {
  siteUrl: string;
  permissionLevel: string | null;
};

export type GoogleAnalyticsProperty = {
  propertyId: string;
  propertyName: string;
  displayName: string;
  accountName: string | null;
  accountDisplayName: string | null;
};

export type DataChatRole = "user" | "assistant";

export type DataChatThread = {
  id: string;
  title: string;
  pinnedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

export type DataChatMessage = {
  id: string;
  role: DataChatRole;
  content: string;
  createdAt: string | null;
  context?: DataChatStructuredPayload | null;
};

export type DataChatEvidence = {
  label: string;
  value: string;
  detail?: string | null;
  sourceType:
    | "visibility"
    | "prompt"
    | "competitor"
    | "citation"
    | "task"
    | "trend"
    | "source"
    | "strategy";
};

export type DataChatRecommendedAction = {
  title: string;
  priority: "High" | "Medium" | "Low";
  reason: string;
  evidence: string;
  sourceType:
    "visibility" | "prompt" | "competitor" | "citation" | "task" | "source";
  actionId?: string | null;
  clusterId?: string | null;
  promptId?: string | null;
  url?: string | null;
};

export type DataChatAnswerComponent = {
  type:
    | "key_takeaway"
    | "opportunity"
    | "warning"
    | "table"
    | "metric_comparison"
    | "prompt_list"
    | "competitor_list"
    | "citation_list"
    | "task_list"
    | "numbered_list"
    | "bullet_list";
  title: string;
  summary?: string | null;
  columns?: string[];
  rows?: Array<Record<string, string | number | null>>;
  items?: string[];
  metrics?: Array<{ label: string; value: string; detail?: string | null }>;
};

export type DataChatStructuredPayload = {
  recommendedActions: DataChatRecommendedAction[];
  evidence: DataChatEvidence[];
  dataLimitations: string[];
  followUpQuestions: string[];
  components: DataChatAnswerComponent[];
  generationStatus?: "pending" | "failed";
  requestId?: string | null;
};

export type DashboardCardSource = DataSourceProvider;
export type DashboardCardVisualization = "metric" | "table" | "bar" | "line";

export type DashboardCard = {
  id: string;
  title: string;
  metricKey: string;
  source: DashboardCardSource;
  visualization: DashboardCardVisualization;
  config: Record<string, any>;
  createdFromPrompt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
};

export type DashboardCardDraft = {
  title: string;
  metricKey: string;
  source?: DashboardCardSource;
  visualization?: DashboardCardVisualization;
  config?: Record<string, any>;
  createdFromPrompt?: string | null;
};

export type DataChatMessageResponse = {
  ok: boolean;
  answer: string;
  followUpQuestions: string[];
  recommendedActions: DataChatRecommendedAction[];
  evidence: DataChatEvidence[];
  dataLimitations: string[];
  components: DataChatAnswerComponent[];
  dashboardCards: DashboardCardDraft[];
  thread?: DataChatThread;
  messages?: DataChatMessage[];
  context?: Record<string, any>;
};

export type DataChatMessageStartResponse = {
  ok: boolean;
  thread: DataChatThread;
  message: DataChatMessage;
};

export type DataChatProgressPayload = {
  brandId: string;
  requestId: string | null;
  userMessageId: string | null;
  stage: "planning" | "querying" | "synthesizing" | "completed" | "failed";
  detail?: string | null;
  tools?: string[];
  completedTools?: number;
  totalTools?: number;
  emittedAt: string;
};

export const authAPI = {
  login: async (data: { idToken: string }): Promise<MeResponse> => {
    const response = await api.post("/auth/login", data);
    return response.data;
  },

  logout: async (): Promise<MeResponse> => {
    const response = await api.post("/auth/logout");
    return response.data;
  },

  setupBrand: async (data: {
    country: string;
    website: string;
    name: string;
    currentFocus: string;
    source?: "manual" | "prefilled";
    companyId?: string | null;
  }): Promise<BrandResponse> => {
    const response = await api.post("/auth/setup/brand", data);
    return response.data;
  },

  getMe: async (): Promise<MeResponse> => {
    const response = await api.post("/auth/me");
    return response.data;
  },

  getMeUsage: async (): Promise<MeUsageResponse> => {
    const response = await api.post("/auth/me/usage");
    return response.data;
  },

  getProviderOverrides: async (): Promise<ProviderOverridesResponse> => {
    const response = await api.post("/auth/me/provider-overrides");
    return response.data;
  },

  updateProviderOverrides: async (payload: {
    providers?: string[];
    usePlanDefaults?: boolean;
  }): Promise<ProviderOverridesResponse> => {
    const response = await api.post(
      "/auth/me/provider-overrides/update",
      payload,
    );
    return response.data;
  },

  sendVerificationEmail: async (): Promise<VerificationEmailSendResponse> => {
    const response = await api.post("/auth/verification-email/send");
    return response.data;
  },

  resolveVerificationToken: async (
    token: string,
  ): Promise<VerificationEmailResolveResponse> => {
    const response = await api.get(
      `/auth/verification-email/resolve/${encodeURIComponent(token)}`,
    );
    return response.data;
  },

  setPrompts: async (
    clusters: {
      clusterId?: string;
      title: string;
      prompts: { text: string; country: string }[];
    }[],
    brandId?: string,
  ): Promise<any> => {
    const payload: { clusters: typeof clusters; brandId?: string } = {
      clusters,
    };
    if (brandId) {
      payload.brandId = brandId;
    }
    const response = await api.post("/auth/setup/prompts", payload);
    return response.data;
  },
};

export const promptsAPI = {
  normalizeCountries: (selectedCountries: string[]): string[] => {
    if (!selectedCountries?.length) return [];
    return Array.from(
      new Set(
        selectedCountries
          .map((country) => country.trim().toUpperCase())
          .filter(Boolean),
      ),
    ).sort();
  },
  generatePrompts: async (
    brandId: string,
    selectedCluster: string | null,
    mode: SuggestedPromptsGenerateMode,
    requestedCount = 6,
  ): Promise<SuggestedPromptsGenerateResponse> => {
    const params: {
      brandId: string;
      clusterId?: string;
      mode: SuggestedPromptsGenerateMode;
      requestedCount: number;
    } = { brandId, mode, requestedCount };
    if (selectedCluster && mode === "single_cluster") {
      params.clusterId = selectedCluster;
    }
    const response = await api.get(`/prompt/generate`, { params });
    return response.data;
  },
  getSuggestedPrompts: async (
    brandId: string,
    clusterId?: string | null,
  ): Promise<{
    suggestedPrompts: {
      text: string;
      country: string;
      clusterId?: string | null;
    }[];
  }> => {
    const params: { brandId: string; clusterId?: string } = { brandId };
    if (clusterId) {
      params.clusterId = clusterId;
    }
    const response = await api.get(`/prompt/suggested`, { params });
    return response.data;
  },
  getPrompts: async (
    brand: Brand,
    selectedCountries: string[],
    providers?: string[],
    days?: number,
    dateRange?: { startDate?: string; endDate?: string } | null,
  ): Promise<{ ok: boolean; clusters: PromptCluster[] }> => {
    const params: {
      brandId: string;
      countries?: string;
      providers?: string;
      days?: number;
      startDate?: string;
      endDate?: string;
    } = {
      brandId: brand.brandId,
    };
    const normalizedCountries =
      promptsAPI.normalizeCountries(selectedCountries);
    if (normalizedCountries.length) {
      params.countries = normalizedCountries.join(",");
    }
    if (providers?.length) {
      params.providers = providers.join(",").toUpperCase();
    }
    if (Number.isFinite(days) && Number(days) > 0) {
      params.days = Number(days);
    }
    if (dateRange?.startDate) {
      params.startDate = dateRange.startDate;
    }
    if (dateRange?.endDate) {
      params.endDate = dateRange.endDate;
    }
    const response = await api.get("/prompt/", { params: params });
    return response.data;
  },
  generateClusters: async (
    brandId: string,
    count = 6,
  ): Promise<{ clusters: GeneratedCluster[] }> => {
    const response = await api.post(`/prompt/generate-clusters`, {
      brandId,
      count,
    });
    return response.data;
  },
  generateClusterPrompts: async (
    brandId: string,
    clusters: GeneratedCluster[],
    countPerCluster = 7,
  ): Promise<{ clusters: GeneratedClusterPrompts[] }> => {
    const response = await api.post(`/prompt/generate-cluster-prompts`, {
      brandId,
      clusters,
      countPerCluster,
    });
    return response.data;
  },
  addPrompt: async (
    prompt: any,
  ): Promise<{ ok: boolean; prompts: Prompt[] }> => {
    const response = await api.post("/prompt/", prompt);
    return response.data;
  },
  updatePromptStatus: async (
    data: Prompt,
    id: string,
  ): Promise<{ ok: boolean; prompts: Prompt[] }> => {
    const response = await api.put(`/prompt/${id}`, data);
    return response.data;
  },
  updateCluster: async (
    id: string,
    data: { title: string; country: string },
  ): Promise<{ ok: boolean; cluster: PromptCluster }> => {
    const response = await api.put(`/prompt/cluster/${id}`, data);
    return response.data;
  },
  deleteCluster: async (id: string): Promise<any> => {
    const response = await api.delete(`/prompt/cluster/${id}`);
    return response.data;
  },
  deletePrompt: async (id: string): Promise<any> => {
    const response = await api.delete(`/prompt/${id}`);
    return response.data;
  },
  deleteSuggestedPrompt: async (id: string): Promise<any> => {
    const response = await api.delete(`/prompt/suggested/${id}`);
    return response.data;
  },
  boostPrompts: async (payload: {
    brandId: string;
    clusterId: string;
    providers?: string[];
    days?: number;
  }): Promise<{
    ok: boolean;
    status?: "ok" | "blocked";
    reason?: string;
    messageId?: string;
    message?: string;
    trial_boosted_cluster_id?: string | null;
    requested_cluster_id?: string | null;
    unlockAt?: string | null;
    cooldownDays?: number | null;
  }> => {
    const response = await api.post("/prompt/boost", payload);
    return response.data;
  },
};

export const visabilityAPI = {
  getRunResults: async (
    brand: Brand,
    days: number,
    providers?: string[],
    promptId?: string,
    includeActionables?: boolean,
    includePageStats?: boolean,
    dateRange?: { startDate: string; endDate: string },
    includeRunResponses?: boolean,
    includeRunData?: boolean,
  ): Promise<{ ok: boolean; data: ChartData }> => {
    const params: {
      brandId: string;
      providers?: string;
      days: number;
      promptId?: string;
      includeActionables?: string;
      includePageStats?: string;
      includeRunResponses?: string;
      includeRunData?: string;
      startDate?: string;
      endDate?: string;
    } = {
      brandId: brand.brandId,
      days,
    };
    if (providers && providers.length) {
      params.providers = providers.join(",").toUpperCase();
    }
    if (promptId) {
      params.promptId = promptId;
    }
    if (includeActionables !== undefined) {
      params.includeActionables = includeActionables ? "true" : "false";
    }
    if (includePageStats !== undefined) {
      params.includePageStats = includePageStats ? "true" : "false";
    }
    if (includeRunResponses !== undefined) {
      params.includeRunResponses = includeRunResponses ? "true" : "false";
    }
    if (includeRunData !== undefined) {
      params.includeRunData = includeRunData ? "true" : "false";
    }
    if (dateRange?.startDate && dateRange?.endDate) {
      params.startDate = dateRange.startDate;
      params.endDate = dateRange.endDate;
    }
    const response = await api.get("/visibility/run/results/", {
      params: params,
    });
    return response.data;
  },
  getPromptExecutions: async (params: {
    brandId: string;
    days: number;
    providers?: string[];
    promptId: string;
    page: number;
    pageSize: number;
    search?: string;
    dateRange?: { startDate: string; endDate: string };
  }): Promise<{
    ok: boolean;
    executions: any[];
    pagination: PaginatedResponseMeta;
    promptProviderStatusHistory?: any[];
    promptProviders?: string[];
  }> => {
    const response = await api.get("/visibility/run/executions", {
      params: {
        brandId: params.brandId,
        days: params.days,
        providers: params.providers?.length
          ? params.providers.join(",").toUpperCase()
          : undefined,
        promptId: params.promptId,
        page: params.page,
        pageSize: params.pageSize,
        search: params.search?.trim() || undefined,
        startDate: params.dateRange?.startDate,
        endDate: params.dateRange?.endDate,
      },
    });
    return response.data;
  },
  exportPromptExecutions: async (params: {
    brandId: string;
    days: number;
    providers?: string[];
    promptId: string;
    search?: string;
    dateRange?: { startDate: string; endDate: string };
  }): Promise<{
    ok: boolean;
    executions: any[];
    total: number;
  }> => {
    const response = await api.get("/visibility/run/executions/export", {
      params: {
        brandId: params.brandId,
        days: params.days,
        providers: params.providers?.length
          ? params.providers.join(",").toUpperCase()
          : undefined,
        promptId: params.promptId,
        search: params.search?.trim() || undefined,
        startDate: params.dateRange?.startDate,
        endDate: params.dateRange?.endDate,
      },
    });
    return response.data;
  },
  getPromptSources: async (params: {
    brandId: string;
    days: number;
    providers?: string[];
    promptId?: string;
    page: number;
    pageSize: number;
    search?: string;
    sourceKind?: "cited" | "mentioned" | "cited-domains";
    urlTypes?: string[];
    domainTypes?: string[];
    dateRange?: { startDate: string; endDate: string };
  }): Promise<{
    ok: boolean;
    sources: any[];
    sourceTypeSummary?: any[];
    pagination: PaginatedResponseMeta;
  }> => {
    const response = await api.get("/visibility/run/sources", {
      params: {
        brandId: params.brandId,
        days: params.days,
        providers: params.providers?.length
          ? params.providers.join(",").toUpperCase()
          : undefined,
        promptId: params.promptId,
        page: params.page,
        pageSize: params.pageSize,
        search: params.search?.trim() || undefined,
        sourceKind: params.sourceKind,
        urlTypes: params.urlTypes?.length
          ? params.urlTypes.join(",")
          : undefined,
        domainTypes: params.domainTypes?.length
          ? params.domainTypes.join(",")
          : undefined,
        startDate: params.dateRange?.startDate,
        endDate: params.dateRange?.endDate,
      },
    });
    return response.data;
  },
  exportPromptSources: async (params: {
    brandId: string;
    days: number;
    providers?: string[];
    promptId?: string;
    search?: string;
    sourceKind?: "cited" | "mentioned" | "cited-domains";
    urlTypes?: string[];
    domainTypes?: string[];
    dateRange?: { startDate: string; endDate: string };
  }): Promise<{
    ok: boolean;
    sources: any[];
    total: number;
  }> => {
    const response = await api.get("/visibility/run/sources/export", {
      params: {
        brandId: params.brandId,
        days: params.days,
        providers: params.providers?.length
          ? params.providers.join(",").toUpperCase()
          : undefined,
        promptId: params.promptId,
        search: params.search?.trim() || undefined,
        sourceKind: params.sourceKind,
        urlTypes: params.urlTypes?.length
          ? params.urlTypes.join(",")
          : undefined,
        domainTypes: params.domainTypes?.length
          ? params.domainTypes.join(",")
          : undefined,
        startDate: params.dateRange?.startDate,
        endDate: params.dateRange?.endDate,
      },
    });
    return response.data;
  },
  getPromptRunDetails: async (params: {
    brandId: string;
    runId: string;
  }): Promise<{ ok: boolean; run: any }> => {
    const response = await api.get("/visibility/run/details", {
      params,
    });
    return response.data;
  },
  getActionables: async (
    brand: Brand,
    days: number,
    providers?: string[],
    promptId?: string,
    options?: { scope?: "foundation" },
  ): Promise<{
    ok: boolean;
    data: {
      actionables: Actionable[];
      citedPagesStats: any;
      promptStatus?: string;
      promptLastRun?: string | null;
      foundation?: FoundationSummary;
      clusterActions?: ClusterAction[];
    };
  }> => {
    const params: {
      brandId: string;
      providers?: string;
      days: number;
      promptId?: string;
      scope?: string;
    } = {
      brandId: brand.brandId,
      days,
    };
    if (providers && providers.length) {
      params.providers = providers.join(",").toUpperCase();
    }
    if (promptId) {
      params.promptId = promptId;
    }
    if (options?.scope) {
      params.scope = options.scope;
    }
    const response = await api.get("/visibility/actionables", {
      params: params,
    });
    return response.data;
  },
  updateActionable: async (payload: {
    brandId: string;
    url: string;
    status?: "toAddress" | "inProgress" | "resolved" | "canceled";
    contactName?: string | null;
    contactEmail?: string | null;
    lastEmailSubject?: string | null;
    lastEmailBody?: string | null;
    lastContactedAt?: string | null;
  }): Promise<any> => {
    const response = await api.patch("/visibility/actionables", payload);
    return response.data;
  },
  updateActionableById: async (payload: {
    id: string;
    brandId: string;
    status?: "toAddress" | "inProgress" | "resolved" | "canceled";
    priority?: "High" | "Medium" | "Low";
    subtasks?: Record<string, any>[] | null;
    contactName?: string | null;
    contactEmail?: string | null;
    lastEmailSubject?: string | null;
    lastEmailBody?: string | null;
    lastContactedAt?: string | null;
    sortIndex?: number | null;
  }): Promise<any> => {
    const { id, ...body } = payload;
    const response = await api.patch(`/visibility/actionables/${id}`, body);
    return response.data;
  },
  refreshCitedPageSnapshot: async (payload: {
    brandId: string;
    url: string;
  }): Promise<{
    ok: boolean;
    status: "queued" | "rate_limited" | "invalid_url";
    normalizedUrl?: string;
    urlType?: string | null;
    availableAt?: string | null;
    queued?: boolean;
    dispatchMode?: "queue" | "direct" | "none";
  }> => {
    const response = await api.post("/visibility/snapshot/refresh", payload);
    return response.data;
  },
  reorderActionables: async (payload: {
    brandId: string;
    columns: Partial<
      Record<
        "toAddress" | "inProgress" | "resolved" | "canceled",
        { id: string; sortIndex?: number }[]
      >
    >;
  }): Promise<any> => {
    const response = await api.patch(
      "/visibility/actionables/reorder",
      payload,
    );
    return response.data;
  },
  runFoundation: async (
    brandId: string,
  ): Promise<{ ok: boolean; messageId?: string; message?: string }> => {
    const response = await api.post("/visibility/foundation/run", { brandId });
    return response.data;
  },
  getStrategySnapshot: async (params: {
    brandId: string;
    clusterId?: string;
    providers?: string[];
    days?: number;
    timeRange?: StrategyTimeRange["type"];
    autoQueue?: boolean;
  }): Promise<{ ok: boolean; data: StrategySnapshotData }> => {
    const response = await api.get("/visibility/strategy/snapshot", {
      params: {
        brandId: params.brandId,
        clusterId: params.clusterId,
        providers: params.providers?.length
          ? params.providers.join(",").toUpperCase()
          : undefined,
        days: params.days,
        timeRange: params.timeRange,
        autoQueue: params.autoQueue,
      },
    });
    return response.data;
  },
  getOnboardingSnapshot: async (params?: {
    brandId?: string;
    sampleSize?: number;
  }): Promise<{ ok: boolean; snapshot: OnboardingSnapshotData }> => {
    const response = await api.get("/visibility/onboarding/snapshot", {
      params: {
        brandId: params?.brandId,
        sampleSize: params?.sampleSize,
      },
    });
    return response.data;
  },
  getStrategyPlan: async (params: {
    brandId: string;
    clusterId?: string;
    providers?: string[];
    days?: number;
    timeRange?: StrategyTimeRange["type"];
    autoQueue?: boolean;
  }): Promise<{ ok: boolean; data: StrategyPlanData }> => {
    const response = await api.get("/visibility/strategy/plan", {
      params: {
        brandId: params.brandId,
        clusterId: params.clusterId,
        providers: params.providers?.length
          ? params.providers.join(",").toUpperCase()
          : undefined,
        days: params.days,
        timeRange: params.timeRange,
        autoQueue: params.autoQueue,
      },
    });
    return response.data;
  },
  getStrategyRegenerateStatus: async (params: {
    brandId: string;
    clusterId?: string;
    providers?: string[];
    days?: number;
    timeRange?: StrategyTimeRange["type"];
    autoQueue?: boolean;
  }): Promise<{ ok: boolean; data: StrategyRegenerateStatusData }> => {
    const response = await api.get("/visibility/strategy/regenerate-status", {
      params: {
        brandId: params.brandId,
        clusterId: params.clusterId,
        providers: params.providers?.length
          ? params.providers.join(",").toUpperCase()
          : undefined,
        days: params.days,
        timeRange: params.timeRange,
        autoQueue: params.autoQueue,
      },
    });
    return response.data;
  },
  regenerateStrategy: async (payload: {
    brandId: string;
    clusterId: string;
    providers?: string[];
    days?: number;
  }): Promise<StrategyRegenerateResponse> => {
    const response = await api.post("/visibility/strategy/regenerate", payload);
    return response.data;
  },
};

export const translationAPI = {
  translate: async (payload: {
    text: string;
    targetLocale: "en" | "uk";
    sourceLocale?: "en" | "uk" | "auto";
    format?: "text" | "html";
  }): Promise<{
    ok: boolean;
    translatedText: string;
    degraded?: boolean;
    degradedReason?: string;
    degradedStatus?: number;
  }> => {
    const response = await api.post("/translation", payload);
    return response.data;
  },
  translateMany: async (payload: {
    texts: string[];
    targetLocale: "en" | "uk";
    sourceLocale?: "en" | "uk" | "auto";
    format?: "text" | "html";
  }): Promise<{
    ok: boolean;
    translatedTexts: string[];
    degraded?: boolean;
    degradedReasons?: string[];
    degradedStatuses?: number[];
  }> => {
    const response = await api.post("/translation", payload);
    return response.data;
  },
};

export const dataChatAPI = {
  getSources: async (
    brandId: string,
  ): Promise<{ ok: boolean; sources: DataChatSource[] }> => {
    const response = await api.get("/visibility/data-chat/sources", {
      params: { brandId },
    });
    return response.data;
  },
  getSearchConsoleSites: async (payload: {
    brandId: string;
  }): Promise<{ ok: boolean; sites: SearchConsoleSite[] }> => {
    const response = await api.post(
      "/visibility/data-chat/integrations/search-console/sites",
      payload,
    );
    return response.data;
  },
  syncSearchConsole: async (payload: {
    brandId: string;
    siteUrl: string;
    days?: number;
  }): Promise<{ ok: boolean; sync: { status: string; rowCount: number } }> => {
    const response = await api.post(
      "/visibility/data-chat/integrations/search-console/sync",
      payload,
    );
    return response.data;
  },
  disconnectSearchConsole: async (payload: {
    brandId: string;
  }): Promise<{ ok: boolean; status: string }> => {
    const response = await api.post(
      "/visibility/data-chat/integrations/search-console/disconnect",
      payload,
    );
    return response.data;
  },
  getGoogleAnalyticsProperties: async (payload: {
    brandId: string;
  }): Promise<{ ok: boolean; properties: GoogleAnalyticsProperty[] }> => {
    const response = await api.post(
      "/visibility/data-chat/integrations/ga4/properties",
      payload,
    );
    return response.data;
  },
  disconnectGoogleAnalytics: async (payload: {
    brandId: string;
  }): Promise<{ ok: boolean; status: string }> => {
    const response = await api.post(
      "/visibility/data-chat/integrations/ga4/disconnect",
      payload,
    );
    return response.data;
  },
  syncGoogleAnalytics: async (payload: {
    brandId: string;
    propertyId: string;
    days?: number;
  }): Promise<{ ok: boolean; sync: { status: string; rowCount: number } }> => {
    const response = await api.post(
      "/visibility/data-chat/integrations/ga4/sync",
      payload,
    );
    return response.data;
  },
  getGoogleOAuthConnectUrl: async (payload: {
    brandId: string;
    provider: Extract<DataSourceProvider, "search_console" | "ga4">;
  }): Promise<{ ok: boolean; url: string }> => {
    const response = await api.get(
      "/visibility/data-chat/integrations/google-oauth/connect",
      { params: payload },
    );
    return response.data;
  },
  startMessage: async (payload: {
    brandId: string;
    requestId: string;
    threadId?: string | null;
    rerunFromMessageId?: string | null;
    message: string;
  }): Promise<DataChatMessageStartResponse> => {
    const response = await api.post("/visibility/data-chat/message/start", {
      brandId: payload.brandId,
      requestId: payload.requestId,
      threadId: payload.threadId || undefined,
      rerunFromMessageId: payload.rerunFromMessageId || undefined,
      message: payload.message,
    });
    return response.data;
  },
  sendMessage: async (payload: {
    requestId: string;
    brandId: string;
    threadId?: string | null;
    userMessageId?: string | null;
    rerunFromMessageId?: string | null;
    message: string;
    providers?: string[];
    days?: number;
    signal?: AbortSignal;
  }): Promise<DataChatMessageResponse> => {
    const response = await api.post(
      "/visibility/data-chat/message",
      {
        requestId: payload.requestId,
        brandId: payload.brandId,
        threadId: payload.threadId || undefined,
        userMessageId: payload.userMessageId || undefined,
        rerunFromMessageId: payload.rerunFromMessageId || undefined,
        message: payload.message,
        providers: payload.providers?.length
          ? payload.providers.join(",").toUpperCase()
          : undefined,
        days: payload.days ? String(payload.days) : undefined,
      },
      { signal: payload.signal },
    );
    return response.data;
  },
  getThreads: async (
    brandId: string,
  ): Promise<{ ok: boolean; threads: DataChatThread[] }> => {
    const response = await api.get("/visibility/data-chat/threads", {
      params: { brandId },
    });
    return response.data;
  },
  getThread: async (
    brandId: string,
    threadId: string,
  ): Promise<{
    ok: boolean;
    thread: DataChatThread;
    messages: DataChatMessage[];
  }> => {
    const response = await api.get(
      `/visibility/data-chat/threads/${threadId}`,
      {
        params: { brandId },
      },
    );
    return response.data;
  },
  updateThreadPin: async (
    brandId: string,
    threadId: string,
    pinned: boolean,
  ): Promise<{ ok: boolean; thread: DataChatThread }> => {
    const response = await api.patch(
      `/visibility/data-chat/threads/${threadId}/pin`,
      { brandId, pinned },
    );
    return response.data;
  },
  updateThreadTitle: async (
    brandId: string,
    threadId: string,
    title: string,
  ): Promise<{ ok: boolean; thread: DataChatThread }> => {
    const response = await api.patch(
      `/visibility/data-chat/threads/${threadId}`,
      { brandId, title },
    );
    return response.data;
  },
  deleteThread: async (
    brandId: string,
    threadId: string,
  ): Promise<{ ok: boolean; deleted: boolean }> => {
    const response = await api.delete(
      `/visibility/data-chat/threads/${threadId}`,
      { params: { brandId } },
    );
    return response.data;
  },
  getDashboardCards: async (
    brandId: string,
  ): Promise<{ ok: boolean; cards: DashboardCard[] }> => {
    const response = await api.get("/visibility/data-chat/dashboard-cards", {
      params: { brandId },
    });
    return response.data;
  },
  createDashboardCard: async (
    brandId: string,
    card: DashboardCardDraft,
  ): Promise<{ ok: boolean; card: DashboardCard }> => {
    const response = await api.post("/visibility/data-chat/dashboard-cards", {
      brandId,
      ...card,
    });
    return response.data;
  },
  deleteDashboardCard: async (
    brandId: string,
    id: string,
  ): Promise<{ ok: boolean; deleted: boolean }> => {
    const response = await api.delete(
      `/visibility/data-chat/dashboard-cards/${id}`,
      { params: { brandId } },
    );
    return response.data;
  },
};

export const alertsAPI = {
  getDailySettings: async (
    brandId: string,
  ): Promise<DailyAlertSettingsResponse> => {
    const response = await api.get("/visibility/alerts/daily-settings", {
      params: { brandId },
    });
    return response.data;
  },
  updateDailySettings: async (payload: {
    brandId: string;
    enabled?: boolean;
    sendTime?: string;
    timezone?: string;
    includeVisibilityChanges?: boolean;
    includeCompetitorMentions?: boolean;
    includeActionsRecommendations?: boolean;
    includePromptStatus?: boolean;
  }): Promise<DailyAlertSettingsResponse> => {
    const response = await api.put(
      "/visibility/alerts/daily-settings",
      payload,
    );
    return response.data;
  },
};

export const supportAPI = {
  sendRequest: async (payload: {
    name: string;
    email: string;
    message: string;
    reason: "general-question" | "bug-report" | "feature-request";
  }): Promise<{ ok: boolean }> => {
    const response = await api.post("/support/request", payload);
    return response.data;
  },
};

export const brandsAPI = {
  setupBrand: async (data: {
    country: string;
    website: string;
    name: string;
    currentFocus: string;
    source?: "manual" | "prefilled";
    companyId?: string | null;
  }): Promise<BrandResponse> => {
    const response = await api.post("/brand/", data);
    return response.data;
  },
  updateBrand: async (
    brandId: string,
    data: { currentFocus?: string; country?: string; providers?: string[] },
  ): Promise<BrandResponse> => {
    const response = await api.put(`/brand/${brandId}`, data);
    return response.data;
  },
  deleteBrand: async (id: string): Promise<any> => {
    const response = await api.delete(`/brand/${id}`);
    return response.data;
  },
  getCollaborators: async (
    companyId?: string | null,
  ): Promise<{
    ok: boolean;
    creator: { id: string; email: string } | null;
    companyMembers: CompanyMemberSummary[];
    collaborators: Collaborator[];
  }> => {
    const response = await api.get("/brand/collaborators", {
      params: companyId ? { companyId } : undefined,
    });
    return response.data;
  },
  deleteCollaborator: async (id: string): Promise<any> => {
    const response = await api.delete(`/brand/collaborators/${id}`);
    return response.data;
  },
  deleteCollaboratorByEmail: async (
    email: string,
    companyId?: string | null,
  ): Promise<any> => {
    const response = await api.delete(
      `/brand/collaborators/manage/${encodeURIComponent(email)}`,
      { params: companyId ? { companyId } : undefined },
    );
    return response.data;
  },
  manageCollaborator: async (payload: {
    email: string;
    roleType: "company" | "brand";
    brandIds?: string[];
    companyId?: string | null;
  }): Promise<any> => {
    const response = await api.post("/brand/collaborators/manage", payload);
    return response.data;
  },
};

export const invitesAPI = {
  createInvite: async (data: {
    email: string;
    roleType: "company" | "brand";
    brandIds?: string[];
    companyId?: string | null;
  }): Promise<{ ok: boolean; inviteId: string }> => {
    const response = await api.post("/invites/", data);
    return response.data;
  },
  resolveInvite: async (token: string): Promise<InviteResolveResponse> => {
    const response = await api.get("/invites/resolve", {
      params: { token },
    });
    return response.data;
  },
  acceptInvite: async (token: string): Promise<InviteAcceptResponse> => {
    const response = await api.post("/invites/accept", { token });
    return response.data;
  },
  listInvites: async (
    companyId?: string | null,
  ): Promise<InviteListResponse> => {
    const response = await api.get("/invites/", {
      params: companyId ? { companyId } : undefined,
    });
    return response.data;
  },
  cancelInvite: async (
    id: string,
    companyId?: string | null,
  ): Promise<{ ok: boolean; id: string }> => {
    const response = await api.post(`/invites/${id}/cancel`, undefined, {
      params: companyId ? { companyId } : undefined,
    });
    return response.data;
  },
};

export const competitorsAPI = {
  getCompetitors: async (
    brandId: string,
    providers?: string[],
    days?: number,
  ): Promise<{
    ok: boolean;
    competitors: any[];
    competitorWidget?: CompetitorDashboardWidgetState;
  }> => {
    const params: { brandId: string; providers?: string; days?: number } = {
      brandId,
    };
    if (providers?.length) {
      params.providers = providers.join(",").toUpperCase();
    }
    if (days) {
      params.days = days;
    }
    const response = await api.get("/competitor/", {
      params,
    });
    return response.data;
  },
  getSuggestedCompetitors: async (
    brandId: string,
    providers?: string[],
    days?: number,
    pagination?: {
      page?: number;
      pageSize?: number;
      search?: string;
    },
  ): Promise<{
    ok: boolean;
    suggested: any[];
    pagination?: PaginatedResponseMeta;
  }> => {
    const params: {
      brandId: string;
      providers?: string;
      days?: number;
      page?: number;
      pageSize?: number;
      search?: string;
    } = { brandId };
    if (providers?.length) {
      params.providers = providers.join(",").toUpperCase();
    }
    if (days) {
      params.days = days;
    }
    if (pagination?.page) {
      params.page = pagination.page;
    }
    if (pagination?.pageSize) {
      params.pageSize = pagination.pageSize;
    }
    if (pagination?.search?.trim()) {
      params.search = pagination.search.trim();
    }
    const response = await api.get("/competitor/suggested/", {
      params,
    });
    return response.data;
  },

  approveCompetitor: async (approved: boolean, id: string): Promise<any> => {
    const response = await api.put(`/competitor/${id}`, { approved });
    return response.data;
  },

  addCompetitor: async (competitor: any): Promise<any> => {
    const response = await api.post(`/competitor/`, competitor);
    return response.data;
  },
};

export const onboardingGuideAPI = {
  getState: async (
    brandId: string,
  ): Promise<{ ok: boolean; state: OnboardingGuideState }> => {
    const response = await api.get("/onboarding-guide/state", {
      params: { brandId },
    });
    return response.data;
  },
  updateState: async (payload: {
    brandId: string;
    stepName?: OnboardingGuideStepName;
    completionSource?: "user" | "system";
    dismissed?: boolean;
    completedOverlaySeen?: boolean;
  }): Promise<{
    ok: boolean;
    state: OnboardingGuideState;
    completedStepName: OnboardingGuideStepName | null;
    completionSource: "user" | "system" | null;
  }> => {
    const response = await api.patch("/onboarding-guide/state", payload);
    return response.data;
  },
};

export const configAPI = {
  getPlans: async (): Promise<Plan[]> => {
    const response = await api.get("/config/plans/");
    return response.data;
  },
};

export const paymentAPI = {
  getCustomerPortal: async (): Promise<any> => {
    const response = await api.get("/payment/portal");
    return response.data;
  },
  subscribe: async (priceId: string): Promise<any> => {
    const response = await api.post("/payment/checkout", { priceId });
    return response.data;
  },
};

export type BillingPlan = "starter" | "growth";
export type BillingInterval = "monthly" | "yearly";

export interface BillingCheckoutResponse {
  checkout_url: string;
  plan: BillingPlan;
  interval: BillingInterval;
  trial_eligible: boolean;
  trial_applied: boolean;
  force_payment: boolean;
}

export interface BillingSubscriptionResponse {
  plan: string | null;
  interval: string | null;
  subscription_status: string | null;
  trial_end: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  can_enter_app: boolean;
  trial_started: boolean;
}

export interface TrialExtensionClaimResponse {
  outcome:
    | "extended"
    | "already_claimed"
    | "active_trial"
    | "paid"
    | "ineligible";
  workspace_id: string;
  previous_subscription_status: string | null;
  extension_days: number;
  extension_expires_at: string | null;
}

export const billingAPI = {
  checkout: async (data: {
    plan: BillingPlan;
    interval: BillingInterval;
    force_payment?: boolean;
  }): Promise<BillingCheckoutResponse> => {
    const response = await api.post("/billing/checkout", data);
    return response.data;
  },
  getSubscription: async (): Promise<BillingSubscriptionResponse> => {
    const response = await api.get("/billing/subscription");
    return response.data;
  },
  confirm: async (sessionId?: string): Promise<BillingSubscriptionResponse> => {
    const payload = sessionId ? { session_id: sessionId } : {};
    const response = await api.post("/billing/confirm", payload);
    return response.data;
  },
  claimTrialExtension: async (): Promise<TrialExtensionClaimResponse> => {
    const response = await api.post("/billing/trial-extension/claim");
    return response.data;
  },
};

export default api;
